import { create } from 'zustand';
import { ChatMessage, ChatStreamRequest } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { fetchChatHistory, streamChatResponse, fetchChatSessionSummaries, ChatSessionSummary, PaginatedChatSessionSummaryResponse } from '../services/chatService';
import { AppError } from '../types/errors';
import { refreshCurrentUserUsage } from '../services/authService';
import useAuthStore from '../store/authStore';
import * as Sentry from '@sentry/react';

interface ChatState {
  sessionId: string;
  messages: ChatMessage[];
  currentAIMessage: ChatMessage | null; // Holds the AI message being streamed
  // currentStreamReader: ReadableStreamDefaultReader<Uint8Array> | null; // To manage the stream reader
  currentStreamController: AbortController | null; // To manage the stream cancellation
  isStreaming: boolean;
  generalError: string | null; // For general errors
  streamError: string | null; // For errors during streaming
  isLoadingHistory: boolean;
  historyError: string | null;
  totalMessages: number;
  currentOffset: number;
  messagesLimit: number;

  // New state for session summaries
  chatHistorySessions: ChatSessionSummary[];
  isLoadingHistorySessions: boolean;
  historySessionsError: string | null;
  sessionSummariesOffset: number;
  totalSessionSummaries: number;
  sessionSummariesLimit: number; // To control pagination for summaries
  hasMoreSessionSummaries: boolean;
  selectedSessionIdForView: string | null; // ID of the session whose messages are currently being viewed

  setSessionId: (sessionId: string) => void;
  startStreaming: (initialUserMessage: ChatMessage) => void;
  appendStreamChunk: (chunk: string) => void;
  finalizeStreaming: () => void;
  setGeneralError: (error: string | null) => void;
  setStreamingError: (errorMsg: string) => void;
  // setCurrentStreamReader: (reader: ReadableStreamDefaultReader<Uint8Array> | null) => void;
  setCurrentStreamController: (controller: AbortController | null) => void;
  fetchAndLoadHistory: (sessionIdToLoad?: string) => Promise<void>;
  initializeSession: () => void;
  sendMessage: (messageContent: string, currentPageUrl?: string) => Promise<void>;

  // New actions for session summaries
  fetchSessionSummaries: (loadMore?: boolean) => Promise<void>;
  setSelectedSessionIdForView: (sessionId: string | null) => void;
  startNewChatSession: () => void;
}

// Helper function to get/set sessionId from sessionStorage
const SESSION_ID_KEY = 'smartie-sessionId';

const initializeSessionId = (): string => {
  let storedSessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!storedSessionId) {
    storedSessionId = uuidv4();
    sessionStorage.setItem(SESSION_ID_KEY, storedSessionId);
    console.log('New session ID generated:', storedSessionId);
  } else {
    console.log('Existing session ID found:', storedSessionId);
  }
  return storedSessionId;
};

export const useChatStore = create<ChatState>((set, get) => ({
  sessionId: initializeSessionId(),
  messages: [],
  currentAIMessage: null,
  // currentStreamReader: null,
  currentStreamController: null,
  isStreaming: false,
  generalError: null,
  streamError: null,
  isLoadingHistory: false,
  historyError: null,
  totalMessages: 0,
  currentOffset: 0,
  messagesLimit: 30,

  // Initial state for session summaries
  chatHistorySessions: [],
  isLoadingHistorySessions: false,
  historySessionsError: null,
  sessionSummariesOffset: 0,
  totalSessionSummaries: 0,
  sessionSummariesLimit: 15, // Default limit for session summaries page
  hasMoreSessionSummaries: true, // Assume more initially until first fetch
  selectedSessionIdForView: null,

  setSessionId: (sessionId) => {
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    set({ sessionId });
  },

  startStreaming: (initialUserMessage) => {
    const userMessageWithMeta: ChatMessage = {
      id: initialUserMessage.id || uuidv4(),
      created_at: initialUserMessage.created_at || new Date().toISOString(),
      ...initialUserMessage,
    };
    set((state) => ({
      messages: [...state.messages, userMessageWithMeta],
      isStreaming: true,
      currentAIMessage: {
        id: uuidv4(),
        sender: 'ai', 
        content: '', 
        isLoading: true, 
        created_at: new Date().toISOString(),
      },
      generalError: null,
      streamError: null,
      historyError: null,
    }));
  },

  appendStreamChunk: (chunk) => {
    // UMW-Debug: Log the type and content of the chunk received by appendStreamChunk
    console.log(`[chatStore] appendStreamChunk called. Type: ${typeof chunk}, Chunk content:`, chunk);
    set((state) => {
      if (!state.currentAIMessage) {
        console.warn("[chatStore] appendStreamChunk: currentAIMessage is null, cannot append.");
        return {};
      }
      // UMW-Debug: Log current AI message content before appending
      console.log("[chatStore] Current AI message content BEFORE append:", state.currentAIMessage.content);
      const newContent = state.currentAIMessage.content + chunk;
      // UMW-Debug: Log new AI message content AFTER append
      console.log("[chatStore] Current AI message content AFTER append:", newContent);
      return {
        currentAIMessage: {
          ...state.currentAIMessage,
          content: newContent,
        },
      };
    });
  },

  finalizeStreaming: () => {
    set((state) => {
      if (!state.currentAIMessage) return { 
        isStreaming: false, 
        // currentStreamReader: null 
        currentStreamController: null // Clear controller on finalize
      };
      const finalAIMessage: ChatMessage = {
        ...state.currentAIMessage,
        isLoading: false,
      };
      // Call refreshCurrentUserUsage after successfully finalizing the stream
      console.log("[ChatStore] FinalizeStreaming: Attempting to call refreshCurrentUserUsage.");
      refreshCurrentUserUsage().catch(error => {
        console.error("[ChatStore] Error refreshing user usage after stream finalization:", error);
        // Decide if any specific error handling in chatStore is needed here
      });
      return {
        messages: [...state.messages, finalAIMessage],
        currentAIMessage: null,
        isStreaming: false,
        // currentStreamReader: null,
        currentStreamController: null, // Clear controller on finalize
        streamError: null,
      };
    });
  },

  setGeneralError: (errorMsg) => set({ 
    generalError: typeof errorMsg === 'string' ? errorMsg : errorMsg ? JSON.stringify(errorMsg) : null, 
    isStreaming: false, 
    currentAIMessage: null, 
    // currentStreamReader: null, 
    currentStreamController: null // Clear controller on general error
  }),

  setStreamingError: (errorMsg) => {
    set((state) => {
      let finalMessages = state.messages;
      if (state.currentAIMessage) {
        const erroredAIMessage: ChatMessage = {
          ...state.currentAIMessage,
          content: state.currentAIMessage.content + `\n[Eroare: Flux întrerupt]`,
          isLoading: false,
        };
        finalMessages = [...state.messages, erroredAIMessage];
      }
      return {
        messages: finalMessages,
        streamError: typeof errorMsg === 'string' ? errorMsg : errorMsg ? JSON.stringify(errorMsg) : 'Eroare necunoscută',
        isStreaming: false,
        currentAIMessage: null,
        // currentStreamReader: null,
        currentStreamController: null, // Clear controller on stream error
      };
    });
  },

  // setCurrentStreamReader: (reader) => set({ currentStreamReader: reader }),
  setCurrentStreamController: (controller) => set({ currentStreamController: controller }),

  fetchAndLoadHistory: async (sessionIdToLoad?: string) => {
    const { sessionId: currentActiveSessionId, messagesLimit, selectedSessionIdForView } = get();
    const targetSessionId = sessionIdToLoad || selectedSessionIdForView || currentActiveSessionId;
    
    if (!targetSessionId) {
      set({ messages: [], totalMessages: 0, currentOffset: 0, isLoadingHistory: false, historyError: "Niciun ID de sesiune pentru a încărca istoricul." });
      return;
    }

    set({ isLoadingHistory: true, historyError: null });

    try {
      const initialOffset = 0; // Always fetch from the beginning for a selected session's messages
      const historyData = await fetchChatHistory(targetSessionId, messagesLimit, initialOffset);
      const enrichedMessages = historyData.messages.map(m => ({ ...m, id: m.id || uuidv4() }));

      set({
        messages: enrichedMessages,
        totalMessages: historyData.totalMessages,
        currentOffset: initialOffset, // Reset offset for messages of this session
        isLoadingHistory: false,
        // If we are loading a specific session for view, it might not be the active `sessionId` for new messages
        // For now, `fetchAndLoadHistory` just loads messages into the view.
        // `selectedSessionIdForView` should be set by the component triggering this.
      });
      console.log(`History loaded for session ${targetSessionId}:`, historyData);
    } catch (err) {
      console.error('Error loading history:', err);
      const appErr = err as AppError;

      if (appErr.status === 404 && appErr.detail?.includes('No history found')) {
        set({
          messages: [],
          totalMessages: 0,
          currentOffset: 0,
          isLoadingHistory: false,
          historyError: null,
        });
        console.log('No history found for session, displaying empty state.');
      } else {
        const errorMessage = appErr.detail || appErr.message || (err instanceof Error ? err.message : 'Eroare la încărcarea istoricului');
        set({ historyError: errorMessage, isLoadingHistory: false });
      }
    }
  },

  initializeSession: () => {
    const sessionId = initializeSessionId();
    set({ 
      sessionId, 
      selectedSessionIdForView: sessionId // Initially, view the active session
    });
  },

  sendMessage: async (messageContent: string, currentPageUrl?: string) => {
    const { 
      sessionId: activeSessionId, // Renamed for clarity
      selectedSessionIdForView,
      startStreaming,
      setGeneralError
      // fetchAndLoadHistory, // Not directly used here
      // setSessionId // Not directly used here
    } = get();

    const currentSessionId = activeSessionId; // Can be const

    // If there's no selected session for viewing, or if the selected one is different from active,
    // and we are about to send a message, it implies starting a new chat or continuing the active one.
    // For simplicity, ensure the active `sessionId` is used for sending.
    if (!selectedSessionIdForView || selectedSessionIdForView !== currentSessionId) {
      // If selectedSessionIdForView is null, it implies a new chat on the current active sessionId
      // If selectedSessionIdForView is different, sending message should switch to active session implicitly
      // This logic might need refinement based on UX for new chat vs. continuing existing viewed chat
      console.log("sendMessage: Aligning selectedSessionIdForView with active sessionId for new message.");
      set({ selectedSessionIdForView: currentSessionId });
    }

    if (!currentSessionId) {
      setGeneralError('ID-ul sesiunii nu este disponibil. Mesajul nu poate fi trimis.');
      return;
    }

    const userMessage: ChatMessage = {
      sender: 'user',
      content: messageContent,
    };

    // Abort any ongoing stream before sending a new message
    const { currentStreamController: existingController } = get();
    if (existingController) {
      console.log('sendMessage: Aborting existing stream before sending new message.');
      existingController.abort();
      set({ currentStreamController: null }); // Clear it after aborting
    }

    startStreaming(userMessage);

    const requestData: ChatStreamRequest = {
      session_id: currentSessionId,
      message: messageContent,
    };
    if (currentPageUrl) {
      requestData.currentPageUrl = currentPageUrl;
    }

    try {
      await streamChatResponse(
        requestData,
        (chunk: string) => get().appendStreamChunk(chunk),
        () => get().finalizeStreaming(),
        (errorMsgOrObj) => {
          // UMW-Debug: Log error received by chatStore's onError handler
          console.error(`[chatStore] sendMessage - onError callback received:`, errorMsgOrObj);
          
          Sentry.captureException(
            typeof errorMsgOrObj === 'string' ? new Error(errorMsgOrObj) : errorMsgOrObj,
            {
              tags: { store: "chatStore", action: "sendMessage", event: "streamError" },
              extra: { originalError: errorMsgOrObj }
            }
          );

          const errorDetail = typeof errorMsgOrObj === 'string' 
            ? errorMsgOrObj 
            : (errorMsgOrObj instanceof Error) ? (errorMsgOrObj as AppError).detail || errorMsgOrObj.message : JSON.stringify(errorMsgOrObj);
          get().setStreamingError(errorDetail);

          // If error is AppError and status is 401/403/429, update authStore
          if (typeof errorMsgOrObj !== 'string' && errorMsgOrObj instanceof Error && 'status' in errorMsgOrObj) {
            const appError = errorMsgOrObj as AppError; // Type assertion
            if (appError.status === 401 || appError.status === 403) {
              useAuthStore.getState().setAuthError(appError.detail || 'Authentication/Authorization error during stream.');
            } else if (appError.status === 429) {
              useAuthStore.getState().setRateLimitError(true, appError.detail || 'Rate limit exceeded during stream.');
            }
          }
        },
        (controller: AbortController | null) => get().setCurrentStreamController(controller)
      );

    } catch (err) {
      console.error('Error in sendMessage calling streamChatResponse:', err);
      const appErr = err as AppError;
      const errorMessage = appErr.detail || appErr.message || (err instanceof Error ? err.message : 'Eroare la trimiterea mesajului');
      get().setStreamingError(errorMessage);
    }
  },

  // --- New actions for session summaries ---
  fetchSessionSummaries: async (loadMore = false) => {
    const { sessionSummariesLimit, sessionSummariesOffset, chatHistorySessions } = get();
    set({ isLoadingHistorySessions: true, historySessionsError: null });

    const offsetToFetch = loadMore ? sessionSummariesOffset : 0;

    try {
      const summaryData: PaginatedChatSessionSummaryResponse = await fetchChatSessionSummaries(
        sessionSummariesLimit,
        offsetToFetch
      );
      
      const newSummaries = summaryData.sessions;
      const newOffset = offsetToFetch + newSummaries.length;
      const allSummaries = loadMore ? [...chatHistorySessions, ...newSummaries] : newSummaries;

      set({
        chatHistorySessions: allSummaries,
        totalSessionSummaries: summaryData.total_sessions,
        sessionSummariesOffset: newOffset,
        hasMoreSessionSummaries: newOffset < summaryData.total_sessions,
        isLoadingHistorySessions: false,
      });
      console.log('Session summaries loaded:', summaryData);
    } catch (err) {
      console.error('Error loading session summaries:', err);
      const appErr = err as AppError;
      const errorMessage = appErr.detail || appErr.message || (err instanceof Error ? err.message : 'Eroare la încărcarea sumarelor sesiunilor');
      set({ historySessionsError: errorMessage, isLoadingHistorySessions: false });
    }
  },

  setSelectedSessionIdForView: (sessionIdToView: string | null) => {
    const { fetchAndLoadHistory } = get(); // Only fetchAndLoadHistory is needed here
    set({ selectedSessionIdForView: sessionIdToView });
    if (sessionIdToView) {
      // If a session is selected for viewing, load its history.
      // This does NOT change the active `sessionId` for sending new messages unless `startNewChatSession` is called.
      fetchAndLoadHistory(sessionIdToView);
    } else {
      // If null, it implies user wants to clear the view or prepare for a new chat.
      // The active `sessionId` is still the one for new messages.
      // We might want to clear messages here, or `fetchAndLoadHistory` handles it with null.
      set({ messages: [], totalMessages: 0, currentOffset:0 }); // Clear messages if null
    }
  },

  startNewChatSession: () => {
    const newSessionId = uuidv4();
    get().setSessionId(newSessionId); // This also updates sessionStorage
    set({
      messages: [],
      totalMessages: 0,
      currentOffset: 0,
      historyError: null,
      streamError: null,
      generalError: null,
      currentAIMessage: null,
      isStreaming: false,
      selectedSessionIdForView: newSessionId, // View the new session
      // Reset relevant parts of session summaries if needed, or let it refresh on next open
      // chatHistorySessions: [], 
      // sessionSummariesOffset: 0,
      // totalSessionSummaries: 0,
      // hasMoreSessionSummaries: true,
    });
    console.log('Started new chat session:', newSessionId);
    // Optionally, immediately fetch (empty) history for the new session, though it will be empty.
    // get().fetchAndLoadHistory(newSessionId);
  },
})); 